import { useCallback, useRef, useState } from 'react'

export type MatterArtifactSelectionRange = {
  from: number
  to: number
  head: number
}

function isSameSelection(selection: MatterArtifactSelectionRange, other: MatterArtifactSelectionRange | null) {
  if (!other) return false
  return selection.from === other.from && selection.to === other.to && selection.head === other.head
}

export function useMatterArtifactSelectionTracking({ disabled, onSelectionClickAway }: { disabled: boolean; onSelectionClickAway?: () => void }) {
  const [committedSelection, setCommittedSelection] = useState<MatterArtifactSelectionRange | null>(null)
  const committedSelectionRef = useRef<MatterArtifactSelectionRange | null>(null)
  const pendingSelectionRef = useRef<MatterArtifactSelectionRange | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const disabledRef = useRef(disabled)
  const onSelectionClickAwayRef = useRef(onSelectionClickAway)
  disabledRef.current = disabled
  onSelectionClickAwayRef.current = onSelectionClickAway

  const handleSelectionChange = useCallback((selection: MatterArtifactSelectionRange | null) => {
    pendingSelectionRef.current = selection
    // Formatting can remap the live ProseMirror range without creating a new selection session.
    // Keep the imperative copy current while React state remains the session-level signal.
    if (committedSelectionRef.current) committedSelectionRef.current = selection
  }, [])

  const clearSelection = useCallback(() => {
    pendingSelectionRef.current = null
    committedSelectionRef.current = null
    setCommittedSelection(null)
  }, [])

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    cleanupRef.current?.()
    cleanupRef.current = null
    if (!node) return

    const commitSelection = (dismissedSelection: MatterArtifactSelectionRange | null = null) => {
      requestAnimationFrame(() => {
        if (disabledRef.current) return
        const nextSelection = pendingSelectionRef.current
        // Clicking the highlighted text can make ProseMirror report the same range again on
        // mouseup. Keep that clickaway dismissed instead of briefly reopening the popover.
        if (dismissedSelection && isSameSelection(dismissedSelection, nextSelection)) {
          pendingSelectionRef.current = null
          return
        }

        committedSelectionRef.current = nextSelection
        setCommittedSelection(nextSelection)
      })
    }

    let startedInEditor = false
    let dismissedSelection: MatterArtifactSelectionRange | null = null

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target
      const isSelectionUI = target instanceof Element && Boolean(target.closest('[data-matter-artifact-selection]'))
      const isInEditor = target instanceof Node && node.contains(target)
      const preservesSelection = target instanceof Element && Boolean(target.closest('[data-preserve-editor-selection]'))
      startedInEditor = isInEditor && !isSelectionUI
      dismissedSelection = null

      if (!isSelectionUI && !disabledRef.current) {
        // Formatting controls close the prompt through Radix but retain the active range and
        // decoration until a true clickaway.
        if (preservesSelection) return

        dismissedSelection = committedSelectionRef.current
        pendingSelectionRef.current = null
        committedSelectionRef.current = null
        setCommittedSelection(null)

        if (dismissedSelection) onSelectionClickAwayRef.current?.()
      }
    }

    const handleMouseUp = () => {
      if (startedInEditor) commitSelection(dismissedSelection)
      startedInEditor = false
      dismissedSelection = null
    }

    const handleKeyUp = () => commitSelection()

    // Clear the previous session before ProseMirror handles this gesture. Its
    // multi-click handler selects text on mousedown; bubbling would erase that
    // new pending range before mouseup can commit it.
    document.addEventListener('mousedown', handleMouseDown, true)
    document.addEventListener('mouseup', handleMouseUp)
    node.addEventListener('keyup', handleKeyUp)

    cleanupRef.current = () => {
      document.removeEventListener('mousedown', handleMouseDown, true)
      document.removeEventListener('mouseup', handleMouseUp)
      node.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  return {
    clearSelection,
    committedSelection,
    containerRef,
    handleSelectionChange,
  }
}
